import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, Users, LogIn } from "lucide-react";

type InviteStatus = "loading" | "valid" | "invalid" | "expired" | "used" | "accepting" | "accepted" | "error" | "needs-login";

interface InviteDetails {
  inviter_name: string | null;
  permission_level: "view" | "full";
  expires_at: string;
}

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [status, setStatus] = useState<InviteStatus>("loading");
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Capture token once on mount — from query params or router state (after login redirect)
  const [token] = useState(() =>
    searchParams.get("token") || (location.state as any)?.token || null
  );

  // Clear token from URL to prevent exposure in browser history/referer
  useEffect(() => {
    if (searchParams.get("token")) {
      window.history.replaceState({}, '', '/accept-invite');
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setError("Token de convite não fornecido");
      return;
    }

    checkInvite();
  }, [token]);

  const checkInvite = async () => {
    try {
      // Check if user is logged in
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setStatus("needs-login");
        return;
      }

      // Fetch invitation details
      const { data: invitation, error: inviteError } = await supabase
        .from("share_invitations")
        .select(`
          *,
          inviter:profiles!share_invitations_inviter_id_fkey(full_name)
        `)
        .eq("token", token)
        .single();

      if (inviteError || !invitation) {
        setStatus("invalid");
        setError("Convite não encontrado ou inválido");
        return;
      }

      // Check if already used
      if (invitation.used_at) {
        setStatus("used");
        setError("Este convite já foi utilizado");
        return;
      }

      // Check if expired
      if (new Date(invitation.expires_at) < new Date()) {
        setStatus("expired");
        setError("Este convite expirou");
        return;
      }

      // Check if email matches
      if (user.email?.toLowerCase() !== invitation.invitee_email.toLowerCase()) {
        setStatus("invalid");
        setError(`Este convite foi enviado para ${invitation.invitee_email}. Faça login com esse email para aceitar.`);
        return;
      }

      setInviteDetails({
        inviter_name: invitation.inviter?.full_name || null,
        permission_level: invitation.permission_level as "view" | "full",
        expires_at: invitation.expires_at,
      });
      setStatus("valid");

    } catch (err: any) {
      console.error("Error checking invite:", err);
      setStatus("error");
      setError(err.message || "Erro ao verificar convite");
    }
  };

  const handleAccept = async () => {
    if (!token) return;

    setStatus("accepting");

    try {
      const { data, error } = await supabase.rpc("accept_share_invitation", {
        invitation_token: token,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; message?: string };

      if (!result.success) {
        throw new Error(result.error || "Erro ao aceitar convite");
      }

      setStatus("accepted");

      toast({
        title: "Convite aceito!",
        description: result.message || "Você agora tem acesso à conta compartilhada",
      });

      // Redirect to dashboard after a delay
      setTimeout(() => {
        navigate("/");
      }, 2000);

    } catch (err: any) {
      console.error("Error accepting invite:", err);
      setStatus("error");
      setError(err.message || "Erro ao aceitar convite");
      
      toast({
        title: "Erro ao aceitar convite",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  const handleLogin = () => {
    navigate("/auth", { state: { pendingInviteToken: token } });
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-muted-foreground">Verificando convite...</p>
          </div>
        );

      case "needs-login":
        return (
          <div className="flex flex-col items-center gap-4 py-8">
            <LogIn className="h-12 w-12 text-primary" />
            <h2 className="text-xl font-semibold">Faça login para continuar</h2>
            <p className="text-muted-foreground text-center">
              Você precisa estar logado para aceitar o convite de compartilhamento.
            </p>
            <Button onClick={handleLogin} className="mt-4">
              <LogIn className="h-4 w-4 mr-2" />
              Fazer Login
            </Button>
          </div>
        );

      case "valid":
        return (
          <div className="flex flex-col items-center gap-4 py-8">
            <Users className="h-12 w-12 text-primary" />
            <h2 className="text-xl font-semibold">Convite de Compartilhamento</h2>
            <p className="text-muted-foreground text-center">
              <strong>{inviteDetails?.inviter_name || "Alguém"}</strong> quer compartilhar 
              a conta financeira com você.
            </p>
            <div className="bg-muted rounded-lg p-4 w-full max-w-xs">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Permissão:</span>
                <span className="font-medium">
                  {inviteDetails?.permission_level === "full" 
                    ? "Acesso Total" 
                    : "Apenas Visualizar"
                  }
                </span>
              </div>
            </div>
            <Button onClick={handleAccept} className="mt-4">
              Aceitar Convite
            </Button>
          </div>
        );

      case "accepting":
        return (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-muted-foreground">Aceitando convite...</p>
          </div>
        );

      case "accepted":
        return (
          <div className="flex flex-col items-center gap-4 py-8">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <h2 className="text-xl font-semibold text-green-600">Convite Aceito!</h2>
            <p className="text-muted-foreground text-center">
              Você agora tem acesso à conta de {inviteDetails?.inviter_name || "seu parceiro"}.
            </p>
            <p className="text-sm text-muted-foreground">
              Redirecionando para o dashboard...
            </p>
          </div>
        );

      case "invalid":
      case "expired":
      case "used":
      case "error":
        return (
          <div className="flex flex-col items-center gap-4 py-8">
            <XCircle className="h-12 w-12 text-destructive" />
            <h2 className="text-xl font-semibold text-destructive">
              {status === "expired" ? "Convite Expirado" : 
               status === "used" ? "Convite Já Utilizado" : 
               "Convite Inválido"}
            </h2>
            <p className="text-muted-foreground text-center">{error}</p>
            <Button variant="outline" onClick={() => navigate("/")} className="mt-4">
              Ir para o Dashboard
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-6 sm:p-4">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center px-4 sm:px-6">
          <CardTitle className="flex items-center justify-center gap-2">
            💰 Finanças
          </CardTitle>
          <CardDescription>
            Compartilhamento de Conta
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
}
